// src/pages/admin/AdminRegistrations.tsx
import React, { useEffect, useState, useMemo } from "react";
import Navbar from "../../components/Navbar";
import {
  getRegistrationsInRange,
  groupRegistrationsByCompetition,
  getRegistrationStats,
  CompetitionStats,
  RegistrationStats,
} from "../../firebase/registrations";
import { getAllCompetitions } from "../../firebase/competitions";
import {
  generateAdminReportPDF,
  PDFCompetitionEntry,
  PDFAthleteEntry,
} from "../../utils/generateAdminReportPDF";
import EditRegistrationModal from "../../components/EditRegistrationModal";
import { useTranslation } from "../../hooks/useTranslation";
import { getFunctions, httpsCallable } from "firebase/functions";
import { invalidateCache, invalidateCachePrefix } from "../../utils/cache";
import { C, FONT } from "../../theme";
import { formatDate as formatDateLocalized } from "../../utils/dateHelpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AthleteEntry {
  id: string;
  referenceCode: string;
  isFree?: boolean;
  /** "online" | "prepaid" | "admin_manual" — added in Phase 15 */
  paymentType?: string;
  coachEmail?: string;
  competitionId: string;
  competitionName: string;
  athleteSnapshot: { firstName: string; lastName: string; dateOfBirth: string };
  club: string;
  name: string;
  dateOfBirth: string;
  category: string;
  division: string;
  weightCategory: string;
  price: number;
  weightCategoriesAll: { male: string[]; female: string[] };
  typesAll: string[];
}

interface ClubEntry {
  clubName: string;
  athletes: AthleteEntry[];
  totalAmount: number;
}

interface CompetitionEntry {
  competitionId: string;
  competitionName: string;
  startDate: any;
  endDate: any;
  status: string;
  clubs: ClubEntry[];
  totalAthletes: number;
  totalClubs: number;
  totalRevenue: number;
  /** Card-payment (mock online) revenue — excludes admin_manual and prepaid */
  onlineRevenue: number;
  /** Number of bank-transfer (admin_manual / isFree) athletes */
  bankAthletes: number;
  /** Sum of prices for bank-transfer athletes */
  bankTotal: number;
  /** Number of prepaid athletes */
  prepaidAthletes: number;
  /** Sum of prices for prepaid athletes (face-value, already covered by the credit package) */
  prepaidTotal: number;
  /** onlineRevenue + bankTotal + prepaidTotal */
  grandTotal: number;
  weightCategories: { male: string[]; female: string[] };
  types: string[];
}

// ---------------------------------------------------------------------------
// Pure transforms — shared by the on-screen memo AND the PDF download so the
// export can be rebuilt from freshly-fetched data without depending on React
// state-update timing. Keep the logic identical to what the UI shows.
// ---------------------------------------------------------------------------

function buildEntries(
  allCompetitions: any[],
  stats: CompetitionStats[]
): CompetitionEntry[] {
  return allCompetitions
    .map((comp: any) => {
      const st = stats.find((s) => s.competitionId === comp.id);
      const compTypes: string[] = comp.types ?? (comp.type ? [comp.type] : []);
      const compWeightCats = comp.weightCategories ?? { male: [], female: [] };

      const clubs: ClubEntry[] = (st?.clubs ?? []).map((club) => ({
        clubName: club.clubName,
        totalAmount: club.totalAmount,
        athletes: club.athletes.map((r) => ({
          id: r.id ?? "",
          referenceCode: r.referenceCode ?? "",
          isFree: (r as any).isFree ?? false,
          paymentType: (r as any).paymentType ?? "online",
          coachEmail: (r as any).coachEmail ?? undefined,
          competitionId: comp.id,
          competitionName: comp.name,
          athleteSnapshot: r.athleteSnapshot,
          club: r.club,
          name: `${r.athleteSnapshot.firstName} ${r.athleteSnapshot.lastName}`,
          dateOfBirth: r.athleteSnapshot.dateOfBirth,
          category: r.category ?? "—",
          division: r.division,
          weightCategory: r.weightCategory,
          price: r.price,
          weightCategoriesAll: compWeightCats,
          typesAll: compTypes,
        })),
      }));

      const allAthletes = clubs.flatMap((c) => c.athletes);

      const onlineRevenue = allAthletes
        .filter((a) => !a.isFree && a.paymentType !== "prepaid" && a.paymentType !== "admin_manual")
        .reduce((s, a) => s + a.price, 0);

      const bankAthletes = allAthletes.filter((a) => a.isFree || a.paymentType === "admin_manual").length;
      const bankTotal = allAthletes
        .filter((a) => a.isFree || a.paymentType === "admin_manual")
        .reduce((s, a) => s + a.price, 0);

      const prepaidAthletes = allAthletes.filter((a) => a.paymentType === "prepaid").length;
      const prepaidTotal = allAthletes
        .filter((a) => a.paymentType === "prepaid")
        .reduce((s, a) => s + a.price, 0);

      return {
        competitionId: comp.id,
        competitionName: comp.name,
        startDate: comp.startDate,
        endDate: comp.endDate,
        status: comp.status ?? "closed",
        clubs,
        totalAthletes: st?.totalAthletes ?? 0,
        totalClubs: st?.totalClubs ?? 0,
        totalRevenue: st?.totalRevenue ?? 0,
        onlineRevenue,
        bankAthletes,
        bankTotal,
        prepaidAthletes,
        prepaidTotal,
        grandTotal: onlineRevenue + bankTotal + prepaidTotal,
        weightCategories: compWeightCats,
        types: compTypes,
      };
    })
    .sort((a, b) => {
      const aT = a.startDate?.toDate?.()?.getTime() ?? 0;
      const bT = b.startDate?.toDate?.()?.getTime() ?? 0;
      return bT - aT;
    });
}

interface EntryFilterOpts {
  q: string;
  payChip: "all" | "online" | "prepaid" | "admin_manual";
  cutoff: Date | null;
  /** Empty = all competitions; otherwise restrict to these competition IDs. */
  selectedCompIds: Set<string>;
}

function applyEntryFilters(
  entries: CompetitionEntry[],
  { q, payChip, cutoff, selectedCompIds }: EntryFilterOpts
): CompetitionEntry[] {
  return entries
    .map((e) => {
      // Explicit championship selection (drives both the view and the PDF).
      if (selectedCompIds.size > 0 && !selectedCompIds.has(e.competitionId)) return null;
      // Date filter — comp.startDate is a Firestore Timestamp here
      if (cutoff) {
        const startMs = e.startDate?.toDate?.()?.getTime() ?? 0;
        if (startMs < cutoff.getTime()) return null;
      }
      // Payment-type filter — operate at athlete level, then prune empty
      // clubs/comps. Keeps the existing grouped UI structure intact.
      let clubs = e.clubs;
      if (payChip !== "all") {
        clubs = e.clubs
          .map((c) => {
            const athletes = c.athletes.filter((a) => {
              const isManual = a.isFree || a.paymentType === "admin_manual";
              const pt = isManual ? "admin_manual" : (a.paymentType ?? "online");
              return pt === payChip;
            });
            return { ...c, athletes, totalAmount: athletes.reduce((s, a) => s + a.price, 0) };
          })
          .filter((c) => c.athletes.length > 0);
      }
      if (payChip !== "all" && clubs.length === 0) return null;
      if (q && !e.competitionName.toLowerCase().includes(q)) return null;
      return { ...e, clubs };
    })
    .filter((e): e is CompetitionEntry => e !== null);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminRegistrations() {
  const { t } = useTranslation();
  const [allCompetitions, setAllCompetitions] = useState<any[]>([]);
  const [stats, setStats] = useState<CompetitionStats[]>([]);
  const [aggregate, setAggregate] = useState<RegistrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedComps, setExpandedComps] = useState<Set<string>>(new Set());
  const [expandedClubs, setExpandedClubs] = useState<Map<string, Set<string>>>(new Map());
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [editingReg, setEditingReg] = useState<AthleteEntry | null>(null);
  const [recomputing, setRecomputing] = useState(false);

  // ── New filters: payment type + date range + expand-all ──────────────────
  type PayChip = "all" | "online" | "prepaid" | "admin_manual";
  type DateRange = "all" | "7d" | "30d";
  const [payChip, setPayChip] = useState<PayChip>("all");
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [allExpanded, setAllExpanded] = useState(false);

  // Explicit championship picker for the view + PDF export. Empty = all.
  const [selectedCompIds, setSelectedCompIds] = useState<Set<string>>(new Set());
  const toggleCompSelected = (id: string) =>
    setSelectedCompIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Year selector — defaults to current year, drives the Firestore query.
  // The heavy "all registrations" fetch is gone: we now read just one
  // calendar year at a time via getRegistrationsInRange, which respects the
  // registeredAt index. Aggregate counts for older years come from the
  // aggregates/registrationStats doc (one read, regardless of data size).
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const loadData = async (year: number) => {
    setLoading(true);
    try {
      const start = new Date(year, 0, 1, 0, 0, 0, 0);
      const end   = new Date(year + 1, 0, 1, 0, 0, 0, 0);
      const [comps, regs, agg] = await Promise.all([
        getAllCompetitions(),
        getRegistrationsInRange(start, end),
        getRegistrationStats(),
      ]);
      setAllCompetitions(comps as any[]);
      setStats(groupRegistrationsByCompetition(regs));
      setAggregate(agg);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(selectedYear); }, [selectedYear]);

  // One-shot realignment of the all-time aggregate with the live registrations.
  // The maintainRegistrationStats trigger only counts writes made after it was
  // deployed, so older data can leave the "All-time" strip undercounting. This
  // calls the admin-only Cloud Function that rebuilds the aggregate from the
  // live collection, then busts the cached read and reloads.
  const handleRecompute = async () => {
    if (recomputing) return;
    setRecomputing(true);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-central2"),
        "recomputeRegistrationStats"
      );
      await fn();
      invalidateCache("registrations:aggregate");
      await loadData(selectedYear);
    } catch (err) {
      console.error("Failed to recompute stats:", err);
      alert("Failed to recompute statistics. Please try again.");
    } finally {
      setRecomputing(false);
    }
  };

  // Build the list of years to offer in the dropdown: every year present in
  // the aggregate doc plus the current year (so the picker is never empty).
  const availableYears = useMemo<number[]>(() => {
    const set = new Set<number>([currentYear]);
    if (aggregate) {
      for (const y of Object.keys(aggregate.byYear)) {
        const n = parseInt(y, 10);
        if (!isNaN(n)) set.add(n);
      }
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [aggregate, currentYear]);

  const formatDate = (ts: any): string => formatDateLocalized(ts);

  const entries = useMemo<CompetitionEntry[]>(
    () => buildEntries(allCompetitions, stats),
    [allCompetitions, stats]
  );

  // Compute the cut-off date for date-range filter (registeredAt is not on
  // AthleteEntry, so we approximate by competition.startDate — same data the
  // admin sees in the row, and matches what "competitions from the last N
  // days" intuitively means).
  const cutoff = useMemo<Date | null>(() => {
    if (dateRange === "all") return null;
    const d = new Date();
    d.setDate(d.getDate() - (dateRange === "7d" ? 7 : 30));
    return d;
  }, [dateRange]);

  const filtered = useMemo<CompetitionEntry[]>(
    () =>
      applyEntryFilters(entries, {
        q: search.trim().toLowerCase(),
        payChip,
        cutoff,
        selectedCompIds,
      }),
    [entries, search, payChip, cutoff, selectedCompIds]
  );

  // `allExpanded` acts as an override at render time — when true, every
  // competition and every club inside it is shown expanded, regardless of
  // the per-row expandedComps / expandedClubs state. When toggled back off,
  // the previous per-row state is restored.

  const toggleComp = (compId: string) => {
    setExpandedComps((prev) => {
      const next = new Set(prev);
      if (next.has(compId)) next.delete(compId);
      else next.add(compId);
      return next;
    });
  };

  const toggleClub = (compId: string, clubName: string) => {
    setExpandedClubs((prev) => {
      const next = new Map(prev);
      const clubs = new Set(next.get(compId) ?? []);
      if (clubs.has(clubName)) clubs.delete(clubName);
      else clubs.add(clubName);
      next.set(compId, clubs);
      return next;
    });
  };

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      // Always export the freshest data (weight changes included): drop the
      // cached registrations for this year and refetch before building, then
      // apply the SAME filters the screen uses (championship picker, payment
      // type, date window, search). Avoids React state-timing surprises.
      invalidateCachePrefix("registrations:");
      const start = new Date(selectedYear, 0, 1, 0, 0, 0, 0);
      const end   = new Date(selectedYear + 1, 0, 1, 0, 0, 0, 0);
      const regs  = await getRegistrationsInRange(start, end);
      const freshStats = groupRegistrationsByCompetition(regs);
      const freshFiltered = applyEntryFilters(buildEntries(allCompetitions, freshStats), {
        q: search.trim().toLowerCase(),
        payChip,
        cutoff,
        selectedCompIds,
      });

      const pdfEntries: PDFCompetitionEntry[] = freshFiltered.map((e) => ({
        competitionName: e.competitionName,
        dateRange: `${formatDate(e.startDate)} – ${formatDate(e.endDate)}`,
        status: e.status,
        totalAthletes: e.totalAthletes,
        totalClubs: e.totalClubs,
        totalRevenue: e.totalRevenue,
        onlineRevenue: e.onlineRevenue,
        bankTotal: e.bankTotal,
        prepaidTotal: e.prepaidTotal,
        clubs: e.clubs.map((c) => ({
          clubName: c.clubName,
          totalAmount: c.totalAmount,
          athletes: c.athletes.map((a) => ({
            name: a.name,
            dateOfBirth: a.dateOfBirth,
            category: a.category,
            division: a.division,
            weightCategory: a.weightCategory,
            price: a.price,
            coachEmail: a.coachEmail,
            isFree: a.isFree ?? false,
            paymentType: a.paymentType as PDFAthleteEntry["paymentType"],
          })),
        })),
      }));
      await generateAdminReportPDF(pdfEntries);
      // Keep the on-screen view consistent with what we just exported.
      setStats(freshStats);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.pageLabel}>{t("admin.allRegistrations.pageLabel")}</div>
            <div style={s.title}>{t("admin.allRegistrations.title")}</div>
            <div style={s.subtitle}>
              {filtered.length} {t("admin.allRegistrations.competitions")}
            </div>
          </div>
          <button
            style={generatingPdf || loading ? s.pdfBtnDisabled : s.pdfBtn}
            onClick={handleDownloadPDF}
            disabled={generatingPdf || loading}
          >
            {generatingPdf
              ? t("admin.allRegistrations.generatingPdf")
              : t("admin.allRegistrations.downloadPdf")}
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder={t("admin.allRegistrations.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={s.searchInput}
        />

        {/* Filter toolbar — wraps on small screens */}
        <div style={s.toolbar}>
          {/* Year selector — drives the underlying Firestore range query.
              Each switch fires exactly one indexed read for that year. */}
          <div style={s.toolbarGroup}>
            <span style={s.toolbarLabel}>Year</span>
            <div style={s.chipGroup}>
              {availableYears.map((y) => (
                <button
                  key={y}
                  style={{ ...s.chip, ...(selectedYear === y ? s.chipActive : {}) }}
                  onClick={() => setSelectedYear(y)}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div style={s.toolbarGroup}>
            <span style={s.toolbarLabel}>{t("common.filter.type")}</span>
            <div style={s.chipGroup}>
              {([
                ["all",          t("common.filter.all")],
                ["online",       t("common.filter.online")],
                ["prepaid",      t("common.filter.prepaid")],
                ["admin_manual", t("common.filter.bank")],
              ] as [PayChip, string][]).map(([key, label]) => (
                <button
                  key={key}
                  style={{ ...s.chip, ...(payChip === key ? s.chipActive : {}) }}
                  onClick={() => setPayChip(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={s.toolbarGroup}>
            <span style={s.toolbarLabel}>{t("common.filter.period")}</span>
            <div style={s.chipGroup}>
              {([
                ["all", t("common.filter.all")],
                ["30d", t("common.filter.last30d")],
                ["7d",  t("common.filter.last7d")],
              ] as [DateRange, string][]).map(([key, label]) => (
                <button
                  key={key}
                  style={{ ...s.chip, ...(dateRange === key ? s.chipActive : {}) }}
                  onClick={() => setDateRange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button
            style={{ ...s.chip, ...(allExpanded ? s.chipActive : {}) }}
            onClick={() => setAllExpanded((v) => !v)}
          >
            {allExpanded
              ? t("admin.allRegistrations.collapseAll")
              : t("admin.allRegistrations.expandAll")}
          </button>
        </div>

        {/* Championship picker — choose one or several to scope BOTH the view
            and the PDF export. None selected = all (the previous behavior). */}
        {entries.length > 0 && (
          <div style={{ ...s.toolbar, marginTop: 8 }}>
            <div style={s.toolbarGroup}>
              <span style={s.toolbarLabel}>Championships</span>
              <div style={{ ...s.chipGroup, maxHeight: 132, overflowY: "auto" }}>
                <button
                  style={{ ...s.chip, ...(selectedCompIds.size === 0 ? s.chipActive : {}) }}
                  onClick={() => setSelectedCompIds(new Set())}
                >
                  {t("common.filter.all")}
                </button>
                {entries.map((e) => (
                  <button
                    key={e.competitionId}
                    style={{ ...s.chip, ...(selectedCompIds.has(e.competitionId) ? s.chipActive : {}) }}
                    onClick={() => toggleCompSelected(e.competitionId)}
                    title={e.competitionName}
                  >
                    {e.competitionName}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All-time aggregate strip — one Firestore read, regardless of size.
            Falls back silently if the aggregate doc hasn't been written yet
            (Cloud Function will populate it on the next registration write). */}
        {aggregate && (
          <div style={s.aggStrip}>
            <div style={s.aggItem}>
              <span style={s.aggNum}>{aggregate.total}</span>
              <span style={s.aggLbl}>All-time</span>
            </div>
            <div style={s.aggDivider} />
            <div style={s.aggItem}>
              <span style={{ ...s.aggNum, color: C.red }}>{aggregate.byType.online}</span>
              <span style={s.aggLbl}>Online</span>
            </div>
            <div style={s.aggDivider} />
            <div style={s.aggItem}>
              <span style={{ ...s.aggNum, color: C.blue }}>{aggregate.byType.admin_manual}</span>
              <span style={s.aggLbl}>Bank</span>
            </div>
            <div style={s.aggDivider} />
            <div style={s.aggItem}>
              <span style={{ ...s.aggNum, color: "#7c3aed" }}>{aggregate.byType.prepaid}</span>
              <span style={s.aggLbl}>Prepaid</span>
            </div>
            {aggregate.byYear[String(selectedYear)] && (
              <>
                <div style={s.aggDivider} />
                <div style={s.aggItem}>
                  <span style={s.aggNum}>{aggregate.byYear[String(selectedYear)].total}</span>
                  <span style={s.aggLbl}>{selectedYear}</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* One-shot realign of the all-time aggregate with the live data.
            Safe to run anytime; admin-only via the Cloud Function. */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={handleRecompute}
            disabled={recomputing}
            title="Rebuild the all-time totals from the live registrations"
            style={{
              backgroundColor: C.surface2,
              color: C.text2,
              border: `1px solid ${C.border2}`,
              borderRadius: 8,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: recomputing ? "not-allowed" : "pointer",
              fontFamily: FONT.body,
              opacity: recomputing ? 0.6 : 1,
            }}
          >
            {recomputing ? "↻ Recomputing…" : "↻ Recompute totals"}
          </button>
        </div>

        {loading && <div style={s.stateMsg}>Loading...</div>}

        {!loading && filtered.length === 0 && (
          <div style={s.emptyBox}>
            <div style={s.emptyIcon}>🏆</div>
            <div>{t("admin.allRegistrations.noCompetitions")}</div>
          </div>
        )}

        {/* Competition list */}
        {!loading &&
          filtered.map((entry) => {
            const compOpen = allExpanded || expandedComps.has(entry.competitionId);
            const hasAthletes = entry.totalAthletes > 0;

            return (
              <div key={entry.competitionId} style={s.compCard}>
                <div
                  style={{ ...s.compRow, cursor: hasAthletes ? "pointer" : "default" }}
                  onClick={() => hasAthletes && toggleComp(entry.competitionId)}
                >
                  <div style={s.compLeft}>
                    <div style={s.compName}>{entry.competitionName}</div>
                    <div style={s.compMeta}>
                      {formatDate(entry.startDate)} – {formatDate(entry.endDate)}
                      <span style={entry.status === "open" ? s.badgeOpen : s.badgeClosed}>
                        {entry.status}
                      </span>
                    </div>
                  </div>
                  <div style={s.compRight}>
                    <span style={hasAthletes ? s.countBadge : s.countBadgeZero}>
                      {entry.totalAthletes} {t("admin.allRegistrations.athletes")}
                    </span>
                    {hasAthletes && (
                      <span style={s.chevron}>{compOpen ? "▲" : "▼"}</span>
                    )}
                  </div>
                </div>

                {compOpen && (
                  <div style={s.compBody}>
                    {/* Summary bar */}
                    <div style={s.summaryBar}>
                      <div style={s.summaryItem}>
                        <span style={s.summaryNum}>{entry.totalAthletes}</span>
                        <span style={s.summaryLbl}>{t("admin.allRegistrations.athletes")}</span>
                      </div>
                      <div style={s.summaryDivider} />
                      <div style={s.summaryItem}>
                        <span style={s.summaryNum}>{entry.totalClubs}</span>
                        <span style={s.summaryLbl}>{t("admin.allRegistrations.clubs")}</span>
                      </div>
                      <div style={s.summaryDivider} />
                      {/* Online revenue (card payments only) */}
                      <div style={s.summaryItem}>
                        <span style={{ ...s.summaryNum, color: C.red }}>
                          {entry.onlineRevenue} GEL
                        </span>
                        <span style={s.summaryLbl}>
                          {t("admin.allRegistrations.onlineRevenue")}
                        </span>
                      </div>
                      <div style={s.summaryDivider} />
                      {/* Bank transfer (admin_manual) */}
                      <div style={s.summaryItem}>
                        <span style={{ ...s.summaryNum, color: C.blue }}>
                          {entry.bankTotal} GEL
                        </span>
                        <span style={s.summaryLbl}>
                          {t("admin.allRegistrations.bankTotal")}
                          {entry.bankAthletes > 0 && (
                            <span style={{ opacity: 0.7 }}> ({entry.bankAthletes})</span>
                          )}
                        </span>
                      </div>
                      {/* Prepaid — only show if any exist */}
                      {entry.prepaidAthletes > 0 && (
                        <>
                          <div style={s.summaryDivider} />
                          <div style={s.summaryItem}>
                            <span style={{ ...s.summaryNum, color: "#7c3aed" }}>
                              {entry.prepaidTotal} GEL
                            </span>
                            <span style={s.summaryLbl}>
                              {t("admin.allRegistrations.prepaidTotal")}
                              <span style={{ opacity: 0.7 }}> ({entry.prepaidAthletes})</span>
                            </span>
                          </div>
                        </>
                      )}
                      <div style={s.summaryDivider} />
                      {/* Grand total */}
                      <div style={s.summaryItem}>
                        <span style={{ ...s.summaryNum, color: C.success }}>
                          {entry.grandTotal} GEL
                        </span>
                        <span style={s.summaryLbl}>{t("admin.allRegistrations.revenue")}</span>
                      </div>
                    </div>

                    {/* Club rows */}
                    {entry.clubs.map((club) => {
                      const clubOpen = allExpanded || expandedClubs.get(entry.competitionId)?.has(club.clubName);
                      return (
                        <div key={club.clubName} style={s.clubSection}>
                          <div style={s.clubRow}>
                            <div style={s.clubInfo}>
                              <span style={s.clubName}>{club.clubName}</span>
                              <span style={s.clubMeta}>
                                {club.athletes.length} {t("admin.allRegistrations.athletes")} · {club.totalAmount} GEL
                              </span>
                            </div>
                            <button
                              style={s.toggleClubBtn}
                              onClick={() => toggleClub(entry.competitionId, club.clubName)}
                            >
                              {clubOpen
                                ? t("admin.allRegistrations.collapseClub")
                                : t("admin.allRegistrations.expandClub")}
                            </button>
                          </div>

                          {clubOpen && (
                            <div style={s.athleteTableWrapper}>
                              <table style={s.athleteTable}>
                                <thead>
                                  <tr>
                                    <th style={s.ath}>#</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.athleteName")}</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.dob")}</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.category")}</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.division")}</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.weight")}</th>
                                    <th style={s.ath}>{t("admin.allRegistrations.price")}</th>
                                    <th style={s.ath}>Edit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {club.athletes.map((athlete, idx) => {
                                    const isPrepaid = athlete.paymentType === "prepaid";
                                    const isBank = athlete.isFree || athlete.paymentType === "admin_manual";
                                    return (
                                      <tr key={athlete.id || idx} style={s.atkRow}>
                                        <td style={{ ...s.atd, color: C.text3, fontSize: "12px" }}>
                                          {idx + 1}
                                        </td>
                                        <td style={{ ...s.atd, fontWeight: 600, color: C.text }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                            {athlete.name}
                                            {isBank && (
                                              <span style={s.freeBadge}>ADMIN</span>
                                            )}
                                            {isPrepaid && (
                                              <span style={s.prepaidBadge}>PREPAID</span>
                                            )}
                                          </div>
                                          {(isBank || isPrepaid) && athlete.coachEmail && (
                                            <div style={s.coachEmailHint}>{athlete.coachEmail}</div>
                                          )}
                                        </td>
                                        <td style={s.atd}>{athlete.dateOfBirth}</td>
                                        <td style={{ ...s.atd, fontWeight: 600 }}>{athlete.category}</td>
                                        <td style={s.atd}>{athlete.division}</td>
                                        <td style={s.atd}>{athlete.weightCategory}</td>
                                        <td style={{
                                          ...s.atd,
                                          fontWeight: 700,
                                          color: isPrepaid ? "#7c3aed" : isBank ? C.blue : C.red,
                                        }}>
                                          {athlete.price > 0 ? `${athlete.price} GEL` : "—"}
                                        </td>
                                        <td style={s.atd}>
                                          <button
                                            style={s.editBtn}
                                            onClick={() => setEditingReg(athlete)}
                                          >
                                            ✏ Edit
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {editingReg && (
        <EditRegistrationModal
          registration={editingReg as any}
          weightCategories={editingReg.weightCategoriesAll}
          types={editingReg.typesAll}
          onClose={() => setEditingReg(null)}
          onSaved={async () => {
            setEditingReg(null);
            await loadData(selectedYear);
          }}
        />
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "40px 24px" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "2px",
    color: C.red, marginBottom: "4px", textTransform: "uppercase",
  },
  title: {
    fontFamily: FONT.display, fontSize: "36px",
    letterSpacing: "1px", color: C.text, marginBottom: "4px", lineHeight: 1,
  },
  subtitle: { color: C.text2, fontSize: "14px" },
  pdfBtn: {
    backgroundColor: C.red, color: "#fff", border: "none",
    borderRadius: "8px", padding: "10px 20px", fontSize: "14px",
    fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.3px",
  },
  pdfBtnDisabled: {
    backgroundColor: C.border, color: C.text3, border: "none",
    borderRadius: "8px", padding: "10px 20px", fontSize: "14px",
    fontWeight: 700, cursor: "not-allowed", whiteSpace: "nowrap",
  },
  searchInput: {
    width: "100%", padding: "10px 14px", border: `1px solid ${C.border2}`,
    borderRadius: "8px", fontSize: "14px", backgroundColor: C.surface2,
    color: C.text, outline: "none", marginBottom: "20px", boxSizing: "border-box",
  },
  stateMsg: { textAlign: "center", padding: "60px", color: C.text2 },
  emptyBox: {
    textAlign: "center", padding: "80px", backgroundColor: C.surface,
    border: `1px dashed ${C.border2}`, borderRadius: "12px", color: C.text2,
  },
  emptyIcon: { fontSize: "48px", marginBottom: "16px" },
  compCard: {
    backgroundColor: C.surface, border: `1px solid ${C.border}`,
    borderRadius: "12px", marginBottom: "12px", overflow: "hidden",
  },
  compRow: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "16px 20px", userSelect: "none",
  },
  compLeft: { display: "flex", flexDirection: "column", gap: "5px" },
  compName: { fontFamily: FONT.display, fontSize: "22px", letterSpacing: "0.5px", color: C.text, lineHeight: 1 },
  compMeta: { fontSize: "13px", color: C.text2, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  badgeOpen: {
    backgroundColor: C.successDim, color: C.success, borderRadius: "12px",
    padding: "2px 8px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
  },
  badgeClosed: {
    backgroundColor: C.surface2, color: C.text3, borderRadius: "12px",
    padding: "2px 8px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
  },
  compRight: { display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 },
  countBadge: {
    backgroundColor: C.redDim, color: C.red, borderRadius: "20px",
    padding: "4px 12px", fontSize: "13px", fontWeight: 700, whiteSpace: "nowrap",
  },
  countBadgeZero: {
    backgroundColor: C.surface2, color: C.text3, borderRadius: "20px",
    padding: "4px 12px", fontSize: "13px", fontWeight: 700, whiteSpace: "nowrap",
  },
  chevron: { color: C.text3, fontSize: "12px" },
  compBody: { borderTop: `1px solid ${C.border}` },
  summaryBar: {
    display: "flex", alignItems: "center", padding: "14px 20px",
    gap: "20px", backgroundColor: C.surface2, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap",
  },
  summaryItem: { display: "flex", flexDirection: "column", alignItems: "center" },
  summaryNum: { fontFamily: FONT.display, fontSize: "22px", color: C.text, lineHeight: 1 },
  summaryLbl: { fontSize: "10px", color: C.text3, textTransform: "uppercase", letterSpacing: "1px", marginTop: "2px" },
  summaryDivider: { width: "1px", height: "32px", backgroundColor: C.border },
  clubSection: { borderBottom: `1px solid ${C.border}` },
  clubRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" },
  clubInfo: { display: "flex", flexDirection: "column", gap: "2px" },
  clubName: { fontSize: "14px", fontWeight: 700, color: C.text },
  clubMeta: { fontSize: "12px", color: C.text2 },
  toggleClubBtn: {
    backgroundColor: C.surface2, border: `1px solid ${C.border2}`,
    borderRadius: "6px", color: C.text2, fontSize: "12px", fontWeight: 600,
    padding: "5px 12px", cursor: "pointer", whiteSpace: "nowrap",
  },
  athleteTableWrapper: { overflowX: "auto", borderTop: `1px solid ${C.border}` },
  athleteTable: { width: "100%", borderCollapse: "collapse" },
  ath: {
    textAlign: "left", padding: "8px 14px", backgroundColor: C.surface2,
    color: C.text3, fontWeight: 700, fontSize: "10px", textTransform: "uppercase",
    letterSpacing: "0.5px", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  },
  atkRow: { borderBottom: `1px solid ${C.border}` },
  atd: { padding: "10px 14px", color: C.text2, fontSize: "13px", verticalAlign: "middle", whiteSpace: "nowrap" },
  freeBadge: {
    backgroundColor: C.blueDim, color: C.blue, borderRadius: "4px",
    padding: "1px 5px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.5px", border: "1px solid #93c5fd",
  },
  prepaidBadge: {
    backgroundColor: "#ede9fe", color: "#7c3aed", borderRadius: "4px",
    padding: "1px 5px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.5px", border: "1px solid #c4b5fd",
  },
  coachEmailHint: { fontSize: "10px", color: C.text3, fontWeight: 400, marginTop: "1px" },
  editBtn: {
    backgroundColor: C.surface2, color: C.text2, border: `1px solid ${C.border2}`,
    borderRadius: "6px", padding: "4px 10px", fontSize: "12px", cursor: "pointer",
    fontWeight: 600, whiteSpace: "nowrap",
  },
  // ── Filter toolbar (chips + expand-all) ─────────────────────────────────
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    alignItems: "center",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "16px",
  },
  toolbarGroup: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  toolbarLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  chipGroup: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: "32px",
  },
  chipActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderColor: C.red,
  },
  // ── All-time aggregate strip ─────────────────────────────────────────────
  aggStrip: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    gap: "16px",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  aggItem: { display: "flex", flexDirection: "column", alignItems: "center" },
  aggNum: { fontFamily: FONT.display, fontSize: "22px", color: C.text, lineHeight: 1 },
  aggLbl: {
    fontSize: "10px",
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginTop: "2px",
  },
  aggDivider: { width: "1px", height: "30px", backgroundColor: C.border },
};
